import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PauseCircle, PlayCircle, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { format } from "date-fns";

interface PausedAccountScreenProps {
  resumesAt: Date | string | null;
}

export function PausedAccountScreen({ resumesAt }: PausedAccountScreenProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [confirmed, setConfirmed] = useState(false);

  const resumeDate = resumesAt ? new Date(resumesAt) : null;

  const resumeMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/subscription/resume-from-pause"),
    onSuccess: () => {
      toast({ title: "Subscription resumed", description: "Welcome back! Your access has been restored." });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
    },
    onError: (err: any) => {
      toast({ title: "Failed to resume", description: err.message || "Something went wrong. Please try again.", variant: "destructive" });
    },
  });

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-6 text-center">
        <div className="flex justify-center">
          <div className="rounded-full bg-muted p-5">
            <PauseCircle className="w-12 h-12 text-muted-foreground" />
          </div>
        </div>

        <div className="space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight">Your account is paused</h1>
          <p className="text-muted-foreground">
            Your subscription is currently on pause. You don't have access to the platform until it resumes.
          </p>
        </div>

        {resumeDate && (
          <Card>
            <CardContent className="pt-5 pb-5">
              <div className="flex items-center gap-3 justify-center text-sm">
                <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="text-muted-foreground">
                  Automatically resumes on{" "}
                  <span className="font-semibold text-foreground">
                    {format(resumeDate, "MMMM d, yyyy")}
                  </span>
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="space-y-3">
          {!confirmed ? (
            <>
              <p className="text-sm text-muted-foreground">
                Want access now? You can resume your subscription early and your billing cycle will restart immediately.
              </p>
              <Button
                variant="default"
                className="w-full"
                onClick={() => setConfirmed(true)}
              >
                <PlayCircle className="w-4 h-4 mr-2" />
                Resume my subscription
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                This will restart your billing cycle immediately. Are you sure?
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setConfirmed(false)}
                  disabled={resumeMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  className="flex-1"
                  onClick={() => resumeMutation.mutate()}
                  disabled={resumeMutation.isPending}
                >
                  {resumeMutation.isPending ? "Resuming..." : "Yes, resume now"}
                </Button>
              </div>
            </>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          Need help? Contact us at{" "}
          <a href="mailto:hello@edufocalgroup.com" className="underline underline-offset-2">
            hello@edufocalgroup.com
          </a>
        </p>
      </div>
    </div>
  );
}
